<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="container-fluid my-3">
    <div class="filter my-3">
      <div class="filter-head">
        <h4>إعدادات إصدار التطبيق</h4>
      </div>

      <p class="text-muted mb-4">
        عند تفعيل التحديث الإجباري، لن يتمكن الطلاب الذين يستخدمون إصدارًا أقدم من التطبيق
        من الاستمرار حتى يقوموا بالتحديث. تتم المقارنة باستخدام رقم الإصدار (Version Code).
      </p>

      <form @submit.prevent="save">
        <div class="row">
          <div class="col-lg-6 col-12">
            <div class="form-group mb-3">
              <label for="app_version">آخر إصدار للتطبيق</label>
              <input id="app_version" type="text" class="form-control" placeholder="مثال: 1.0.3"
                     v-model.trim="form.app_version" />
              <small class="text-muted">اسم الإصدار الظاهر للطالب (مثال: 1.0.3).</small>
            </div>
          </div>

          <div class="col-lg-6 col-12">
            <div class="form-group mb-3">
              <label for="app_version_code">رقم الإصدار (Version Code)</label>
              <input id="app_version_code" type="number" min="1" step="1" class="form-control" placeholder="مثال: 4"
                     v-model.number="form.app_version_code" />
              <small class="text-muted">القيمة المعتمدة للمقارنة (رقم صحيح ≥ 1).</small>
            </div>
          </div>

          <div class="col-12">
            <div class="form-group mb-3">
              <label for="update_url">رابط التحديث</label>
              <input id="update_url" type="url" class="form-control" placeholder="https://play.google.com/store/apps/details?id=..."
                     v-model.trim="form.update_url" />
              <small class="text-muted">رابط متجر Google Play للتطبيق.</small>
            </div>
          </div>

          <div class="col-12">
            <div class="form-group mb-3">
              <label for="update_message">رسالة التحديث</label>
              <textarea id="update_message" rows="3" class="form-control"
                        v-model.trim="form.update_message"
                        placeholder="يوجد إصدار جديد من التطبيق، يرجى تحديث التطبيق للاستمرار في استخدام المنصة."></textarea>
            </div>
          </div>

          <div class="col-12">
            <div class="form-check form-switch mb-4">
              <input id="force_update" class="form-check-input" type="checkbox" role="switch"
                     v-model="form.force_update" />
              <label class="form-check-label" for="force_update">تفعيل التحديث الإجباري</label>
            </div>
          </div>
        </div>

        <button type="submit" class="btn btn-primary" :disabled="saving">
          <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
          حفظ الإعدادات
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/global/breadcrumb.vue';

const VERSION_NAME_RE = /^\d+(\.\d+){0,3}$/;

export default {
  name: 'AppVersionSettings',
  components: { Breadcrumb },
  data() {
    return {
      saving: false,
      form: {
        app_version: '',
        app_version_code: null,
        force_update: false,
        update_url: '',
        update_message: '',
      },
      breadcrumbs: [
        { name: 'الرئيسية', path: '/' },
        { name: 'إعدادات إصدار التطبيق', path: '/settings/app-version' },
      ],
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      axios.get('/app-version-settings').then(({ data }) => {
        const d = data.data || {};
        this.form.app_version = d.app_version ?? '';
        this.form.app_version_code = d.app_version_code ?? null;
        this.form.force_update = !!d.force_update;
        this.form.update_url = d.update_url ?? '';
        this.form.update_message = d.update_message ?? '';
      }).catch(() => {
        // Leave defaults; the admin can still configure from scratch.
      });
    },
    validate() {
      if (!VERSION_NAME_RE.test(this.form.app_version || '')) {
        Swal.fire({ position: 'center', icon: 'error', title: 'صيغة رقم الإصدار غير صحيحة (مثال: 1.0.3)' });
        return false;
      }
      const code = Number(this.form.app_version_code);
      if (!Number.isInteger(code) || code < 1) {
        Swal.fire({ position: 'center', icon: 'error', title: 'رقم الإصدار (Version Code) يجب أن يكون رقمًا صحيحًا 1 أو أكبر' });
        return false;
      }
      if (this.form.update_url) {
        try { new URL(this.form.update_url); } catch (e) {
          Swal.fire({ position: 'center', icon: 'error', title: 'رابط التحديث غير صالح' });
          return false;
        }
      }
      return true;
    },
    save() {
      if (!this.validate()) return;
      this.saving = true;
      axios.put('/app-version-settings', {
        app_version: this.form.app_version,
        app_version_code: Number(this.form.app_version_code),
        force_update: this.form.force_update,
        update_url: this.form.update_url || null,
        update_message: this.form.update_message || null,
      }).then(({ data }) => {
        Swal.fire({ position: 'center', icon: 'success', title: data.message || 'تم الحفظ بنجاح' });
      }).catch((err) => {
        const msg = err.response?.data?.message
          || Object.values(err.response?.data?.errors || {})[0]?.[0]
          || 'حدث خطأ أثناء الحفظ';
        Swal.fire({ position: 'center', icon: 'error', title: msg });
      }).finally(() => {
        this.saving = false;
      });
    },
  },
};
</script>
